import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import employeesCollection from 'modules/employees/store/collections/employees'
import { useDeleteLocation } from 'modules/locations/hooks/useDeleteLocation'
import { useLocations } from 'modules/locations/hooks/useLocations'
import { useUpdateLocation } from 'modules/locations/hooks/useUpdateLocation'
import HeadquarterForm from 'modules/locations/lib/forms/HeadquarterForm'
import LocationSelectForm from 'modules/locations/lib/forms/LocationSelectForm'
import { locationActiveEmployees } from 'modules/locations/lib/locationEmployees'
import locationCollection from 'modules/locations/store/collections/locations'

interface Props {
  locationId: number
  onClose: () => void
}

const RemoveLocationModal = ({ locationId, onClose }: Props) => {
  const { deleteLocation } = useDeleteLocation()
  const { updateLocation } = useUpdateLocation()
  const { locations } = useLocations()
  const location = locations.find((item) => item.id === locationId)
  const locationForm = useLazyRef(() => new LocationSelectForm({ locationId })).current
  const headquarterForm = useLazyRef(() => new HeadquarterForm({ locationId })).current
  const description = useMemo((): string => {
    if (!location) return ''

    if (hasEmployees && location.main) {
      return i18n.t('settings.locations.remove.with_employees_and_headquarter')
    } else if (hasEmployees && !location.main) {
      return i18n.t('settings.locations.remove.with_employees')
    }

    return i18n.t('settings.locations.remove.description')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const options = useMemo(() => {
    if (!location) return []

    return locations
      .filter((loc) => location.id !== loc.id)
      .map((location) => ({
        label: location.name,
        value: location.id,
      }))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!location) return null

  const locationStore = locationCollection.get(Number(locationId))

  const hasEmployees =
    locationStore && !!locationActiveEmployees(locationStore, employeesCollection).length

  const disabled = (): boolean => {
    return (
      locations.length < 2 ||
      (!!hasEmployees && !locationForm.get('location_id').value) ||
      (location.main && !headquarterForm.get('location_id').value)
    )
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    if (!location) return null

    const employees = locationStore
      ? locationActiveEmployees(locationStore, employeesCollection)
      : []

    const isMainLocation = location.main

    const newMainLocation = locations.find(
      (item) => item.id == Number(headquarterForm.get('location_id').value)
    )

    if (isMainLocation && newMainLocation) {
      await updateLocation({ id: newMainLocation.id, main: true })
    }

    if (employees.length) {
      const promises = employees.map((employee) =>
        employee.save({
          location_id: locationForm.get('location_id').value,
        })
      )
      await asyncAction(() => Promise.all(promises), {
        successFlash: false,
      })
    }

    await deleteLocation({ id: location.id })
    closePortal()
  }

  if (!location) return null

  return (
    <Modal
      illustration="location-pin-remove"
      title={i18n.t('settings.locations.remove.title', {
        name: location.name,
      })}
      description={description}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={disabled()} type="negative" submit>
          {i18n.t('settings.locations.remove.submit')}
        </Button>
      }
    >
      {location && location.main && (
        <FormRow>
          <Select
            none={i18n.t('common.none')}
            field={headquarterForm.get('location_id')}
            label={i18n.t('common.new_headquarter')}
            options={options}
          />
        </FormRow>
      )}
      {hasEmployees && (
        <FormRow>
          <Select
            none={i18n.t('common.none')}
            field={locationForm.get('location_id')}
            label={i18n.t('common.location')}
            options={options}
          />
        </FormRow>
      )}
    </Modal>
  )
}

export default observer(RemoveLocationModal)
